import { useTranslation } from "react-i18next";

const stats = [
  { num: "500+", enLabel: "Homes Cleaned", esLabel: "Hogares Limpiados" },
  { num: "5.0★", enLabel: "Average Rating", esLabel: "Calificación Promedio" },
  { num: "3+", enLabel: "Years Serving FL", esLabel: "Años en Florida" },
  { num: "100%", enLabel: "Satisfaction Rate", esLabel: "Tasa de Satisfacción" },
];

const features = [
  {
    icon: "ri-home-heart-line",
    enTitle: "Family-Owned Business",
    esTitle: "Negocio Familiar",
    enDesc: "We treat your home like our own. Personal care and genuine trust in every single job.",
    esDesc: "Tratamos tu hogar como el nuestro. Cuidado personal y confianza genuina en cada trabajo.",
    color: "bg-emerald-50 text-emerald-600",
  },
  {
    icon: "ri-focus-3-line",
    enTitle: "Attention to Detail",
    esTitle: "Atención al Detalle",
    enDesc: "We don't cut corners. Every surface, every corner gets the attention it truly deserves.",
    esDesc: "No tomamos atajos. Cada superficie, cada rincón recibe la atención que merece.",
    color: "bg-amber-50 text-amber-600",
  },
  {
    icon: "ri-shield-check-line",
    enTitle: "Reliable & Punctual",
    esTitle: "Confiables y Puntuales",
    enDesc: "We show up on time, every time. You can count on us — no surprises, no excuses.",
    esDesc: "Llegamos a tiempo, siempre. Puedes contar con nosotros — sin sorpresas, sin excusas.",
    color: "bg-sky-50 text-sky-600",
  },
  {
    icon: "ri-leaf-line",
    enTitle: "Eco-Friendly Products",
    esTitle: "Productos Ecológicos",
    enDesc: "Safe for your family and pets. We use non-toxic, environmentally responsible cleaning products.",
    esDesc: "Seguros para tu familia y mascotas. Usamos productos de limpieza no tóxicos y responsables.",
    color: "bg-green-50 text-green-600",
  },
  {
    icon: "ri-tools-line",
    enTitle: "All-in-One Solution",
    esTitle: "Solución Todo en Uno",
    enDesc: "Cleaning + repairs under one roof. No need to hire multiple companies for your home.",
    esDesc: "Limpieza + reparaciones bajo un mismo techo. Sin necesidad de contratar varias empresas.",
    color: "bg-purple-50 text-purple-600",
  },
  {
    icon: "ri-customer-service-2-line",
    enTitle: "Always Responsive",
    esTitle: "Siempre Disponibles",
    enDesc: "Quick replies via WhatsApp or phone. We're here when you need us, no long waits.",
    esDesc: "Respuestas rápidas por WhatsApp o teléfono. Estamos aquí cuando nos necesitas.",
    color: "bg-rose-50 text-rose-600",
  },
];

const whoWeHelp = [
  { icon: "ri-group-line", en: "Busy Families", es: "Familias Ocupadas" },
  { icon: "ri-building-line", en: "Airbnb Hosts", es: "Anfitriones Airbnb" },
  { icon: "ri-home-2-line", en: "Property Owners", es: "Propietarios" },
  { icon: "ri-heart-line", en: "Elderly Residents", es: "Adultos Mayores" },
  { icon: "ri-briefcase-line", en: "Busy Professionals", es: "Profesionales Ocupados" },
  { icon: "ri-star-line", en: "Anyone Who Wants Clean", es: "Quien Quiera Limpieza" },
];

const WhyChooseUs = () => {
  const { i18n } = useTranslation();
  const isEs = i18n.language === "es";

  return (
    <>
      {/* Stats Bar */}
      <section className="bg-brand-dark py-10">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-0 md:divide-x md:divide-white/10">
            {stats.map((s, i) => (
              <div key={i} className="flex flex-col items-center text-center md:px-8">
                <span className="font-heading font-black text-white text-3xl md:text-4xl">{s.num}</span>
                <span className="font-body text-white/50 text-xs mt-1 tracking-wide uppercase">
                  {isEs ? s.esLabel : s.enLabel}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex flex-col lg:flex-row gap-16 items-start">
            {/* Left sticky text */}
            <div className="w-full lg:w-1/3 lg:sticky lg:top-28">
              <span className="inline-block text-brand-green font-body text-sm font-semibold tracking-widest uppercase mb-3">
                {isEs ? "Nuestra Diferencia" : "Our Difference"}
              </span>
              <h2 className="font-heading font-black text-brand-dark text-3xl md:text-4xl lg:text-5xl leading-tight mb-5">
                {isEs ? "¿Por Qué\nElegirnos?" : "Why Choose\nUs?"}
              </h2>
              <p className="font-body text-gray-500 text-sm leading-relaxed mb-8">
                {isEs
                  ? "No somos solo un servicio de limpieza. Somos tu equipo de confianza para mantener tu hogar en perfectas condiciones."
                  : "We're not just a cleaning service. We're your trusted team to keep your home in perfect condition."}
              </p>
              <div className="relative rounded-2xl overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=happy%20professional%20cleaning%20woman%20smiling%20in%20uniform%20holding%20cleaning%20supplies%20bright%20modern%20home%20Florida%20satisfied%20customer%20service&width=500&height=600&seq=why_img1&orientation=portrait"
                  alt="Professional cleaning team"
                  className="w-full h-72 object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-brand-dark/50 to-transparent"></div>
                <div className="absolute bottom-4 left-4 right-4">
                  <div className="bg-white/95 backdrop-blur-sm rounded-xl p-3 flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center bg-brand-green rounded-full">
                      <i className="ri-verified-badge-line text-white text-lg"></i>
                    </div>
                    <div>
                      <p className="font-heading font-bold text-brand-dark text-xs">
                        {isEs ? "Servicio Verificado" : "Verified Service"}
                      </p>
                      <p className="font-body text-gray-500 text-xs">
                        {isEs ? "Royal Palm Beach, FL" : "Royal Palm Beach, FL"}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right: Feature grid */}
            <div className="w-full lg:w-2/3 grid grid-cols-1 sm:grid-cols-2 gap-5">
              {features.map((f, i) => (
                <div key={i} className="border border-gray-100 rounded-2xl p-6 hover:border-brand-green/30 transition-colors">
                  <div className={`w-12 h-12 flex items-center justify-center rounded-xl mb-4 ${f.color}`}>
                    <i className={`${f.icon} text-xl`}></i>
                  </div>
                  <h3 className="font-heading font-bold text-brand-dark text-base mb-2">
                    {isEs ? f.esTitle : f.enTitle}
                  </h3>
                  <p className="font-body text-gray-500 text-sm leading-relaxed">
                    {isEs ? f.esDesc : f.enDesc}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Who We Help */}
      <section className="py-16 bg-brand-beige">
        <div className="max-w-7xl mx-auto px-4 md:px-6 text-center">
          <span className="inline-block text-brand-green font-body text-sm font-semibold tracking-widest uppercase mb-3">
            {isEs ? "Nuestros Clientes" : "Our Clients"}
          </span>
          <h2 className="font-heading font-black text-brand-dark text-3xl md:text-4xl mb-3">
            {isEs ? "¿A Quién Ayudamos?" : "Who We Help"}
          </h2>
          <p className="font-body text-gray-500 text-sm max-w-lg mx-auto mb-10">
            {isEs
              ? "Servimos a una amplia variedad de clientes en Royal Palm Beach y alrededores."
              : "We serve a wide range of clients across Royal Palm Beach and surrounding areas."}
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            {whoWeHelp.map((item, i) => (
              <div key={i} className="flex items-center gap-2.5 bg-white border border-gray-100 rounded-full px-5 py-2.5">
                <div className="w-7 h-7 flex items-center justify-center">
                  <i className={`${item.icon} text-brand-green text-base`}></i>
                </div>
                <span className="font-body font-medium text-brand-dark text-sm whitespace-nowrap">
                  {isEs ? item.es : item.en}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
};

export default WhyChooseUs;
